import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plug, Copy, RefreshCw, Send, Eye, CheckCircle2, XCircle, Clock, RotateCw } from "lucide-react";
import api from "@/lib/api";

const FIELDS = [
  { id: "api_key_test", label: "WTE_API_KEY_TEST (clé d'API — environnement de test)" },
  { id: "api_key_prod", label: "WTE_API_KEY_PROD (clé d'API — production)" },
  { id: "outbound_secret", label: "Secret HMAC sortant (à coller dans les secrets de Sydney)" },
  { id: "inbound_secret", label: "Secret HMAC entrant (Sydney signe ses webhooks avec)" },
];

const STATUS_ICON = {
  delivered: <CheckCircle2 size={14} className="text-emerald-600" />,
  failed: <XCircle size={14} className="text-red-600" />,
  pending: <Clock size={14} className="text-amber-500" />,
  skipped: <Clock size={14} className="text-muted-foreground" />,
};

export const SydneyIntegrationPanel = () => {
  const [data, setData] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sydney/status");
      setData(data);
      setUrl(data.credentials?.webhook_url || "");
    } catch {
      toast.error("Impossible de charger l'intégration Sydney.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = (value) => { navigator.clipboard?.writeText(value || ""); toast.success("Copié dans le presse-papiers."); };

  const reveal = async (field) => {
    try {
      const { data } = await api.post(`/admin/sydney/reveal?field=${field}`);
      setRevealed((r) => ({ ...r, [field]: data.value }));
    } catch { toast.error("Affichage impossible."); }
  };

  const rotate = async (field) => {
    if (!window.confirm("Régénérer cette clé ? Sydney devra être mis à jour immédiatement.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/sydney/rotate?what=${field}`);
      setRevealed((r) => ({ ...r, [field]: data.value }));
      toast.success("Nouvelle clé générée — copiez-la maintenant dans Sydney.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de rotation."); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/sydney/config", { webhook_url: url });
      toast.success("URL du webhook enregistrée.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement."); }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sydney/test-webhook");
      const d = data.last_delivery || {};
      if (d.status === "delivered") toast.success("Webhook de test accepté par Sydney (HTTP 2xx).");
      else if (d.status === "skipped") toast.error("Aucune URL de webhook Sydney configurée.");
      else toast.error(`Sydney a refusé : HTTP ${d.status_code} — ${(d.last_error || "").slice(0, 120)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi."); }
    setBusy(false);
  };

  if (!data) return <p className="p-6 text-sm text-muted-foreground">Chargement de l'intégration…</p>;
  const c = data.credentials || {};

  return (
    <div className="space-y-4" data-testid="sydney-panel">
      <div className="bg-white border border-border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center"><Plug size={18} /></div>
          <div>
            <p className="text-navy font-medium">Intégration Sydney Live Control Center</p>
            <p className="text-xs text-muted-foreground">
              {c.enabled ? "Active" : "Désactivée"} · {data.stats?.delivered || 0} livré(s) · {data.stats?.pending || 0} en attente · {data.stats?.failed || 0} échec(s)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="sydney-refresh" onClick={load} className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm text-navy hover:bg-navy/5 transition-colors">
            <RefreshCw size={16} /> Rafraîchir
          </button>
          <button data-testid="sydney-test-webhook" onClick={test} disabled={busy}
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-white px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Send size={16} /> Tester le webhook
          </button>
        </div>
      </div>

      <div className="bg-white border border-border p-5 space-y-4">
        <div>
          <p className="text-navy font-medium text-sm">Identifiants à configurer dans Sydney</p>
          <p className="text-xs text-muted-foreground mt-1">Les clés sont masquées par défaut. Cliquez sur l'œil pour les afficher le temps de les copier.</p>
        </div>
        <div className="border border-border p-4">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">WTE_API_BASE_URL</p>
          <code data-testid="sydney-base-url" className="block mt-2 text-xs bg-navy/5 text-navy px-3 py-2 font-mono truncate">{data.base_url}</code>
          <p className="text-[10px] text-muted-foreground mt-2">Test de connexion : GET {data.base_url}/sydney/health</p>
        </div>

        {FIELDS.map((f) => (
          <div key={f.id} className="border border-border p-4">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{f.label}</p>
            <div className="flex items-center gap-2 mt-2">
              <code data-testid={`sydney-${f.id}`} className="flex-1 min-w-0 truncate text-xs bg-navy/5 text-navy px-3 py-2 font-mono">
                {revealed[f.id] || c[f.id] || "—"}
              </code>
              <button type="button" data-testid={`sydney-${f.id}-reveal`} onClick={() => reveal(f.id)}
                className="p-2 text-muted-foreground hover:text-navy transition-colors" title="Afficher en clair">
                <Eye size={16} />
              </button>
              <button type="button" data-testid={`sydney-${f.id}-copy`} onClick={() => copy(revealed[f.id])}
                disabled={!revealed[f.id]}
                className="p-2 text-muted-foreground hover:text-gold transition-colors disabled:opacity-40" title="Copier (affichez d'abord)">
                <Copy size={16} />
              </button>
              <button type="button" data-testid={`sydney-${f.id}-rotate`} onClick={() => rotate(f.id)} disabled={busy}
                className="p-2 text-muted-foreground hover:text-red-600 transition-colors" title="Régénérer">
                <RotateCw size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border p-5">
        <p className="text-navy font-medium text-sm">URL du webhook Sydney (destination des événements WTE)</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <input data-testid="sydney-webhook-url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://sydney-live-control.preview.emergentagent.com/api/webhooks/wte"
            className="flex-1 border border-border px-3 py-2 text-sm text-navy focus:outline-none focus:border-gold" />
          <button data-testid="sydney-save-url" onClick={save} disabled={busy}
            className="bg-navy hover:bg-navy/90 text-white px-5 py-2 text-sm transition-colors disabled:opacity-50">Enregistrer</button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Signature : <code className="font-mono">X-WTE-Signature: t=&lt;timestamp&gt;,v1=&lt;hmac_sha256&gt;</code> · relances automatiques (5 tentatives sur 24 h)
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(data.events || []).map((e) => (
            <span key={e} data-testid={`sydney-event-${e}`} className="text-[10px] uppercase tracking-widest bg-gold/10 text-gold px-2 py-1">{e}</span>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border">
        <p className="text-navy font-medium text-sm p-5 pb-3">Journal des webhooks envoyés</p>
        <div className="divide-y divide-border" data-testid="sydney-deliveries">
          {(data.deliveries || []).length === 0 && <p className="p-5 text-sm text-muted-foreground">Aucun envoi pour le moment.</p>}
          {(data.deliveries || []).map((d) => (
            <div key={d.event_id} className="p-4">
              <p className="text-navy text-sm font-medium flex items-center gap-2">
                {STATUS_ICON[d.status] || null} {d.event_type}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(d.created_at).toLocaleString("fr-FR")} · {d.attempts} tentative(s)
                {d.status_code ? ` · HTTP ${d.status_code}` : ""}
                {d.next_attempt_at ? ` · prochaine tentative ${new Date(d.next_attempt_at).toLocaleString("fr-FR")}` : ""}
              </p>
              {d.last_error && <p className="text-xs text-red-600 mt-1 truncate">{d.last_error}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border">
        <p className="text-navy font-medium text-sm p-5 pb-3">Événements reçus de Sydney</p>
        <div className="divide-y divide-border" data-testid="sydney-inbound">
          {(data.inbound || []).length === 0 && <p className="p-5 text-sm text-muted-foreground">Aucun événement reçu.</p>}
          {(data.inbound || []).map((e) => (
            <div key={e.id} className="p-4">
              <p className="text-navy text-sm font-medium">{e.event}</p>
              <p className="text-xs text-muted-foreground mt-1">{new Date(e.received_at).toLocaleString("fr-FR")}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
